import { type ReactNode, useCallback, useState } from 'react'

import { useOlarkHelper } from '@fv/client-components'
import type { PropsWithLoad } from '@fv/client-types'

import { ShipperSlider } from '@/components/ShipperSlider'
import { supportMessage, supportMessageQuestions } from '@/constants'
import { ConfirmationPanel } from '@/features/confirm/ConfirmationPanel'
import ListingDocumentsPanel from '@/features/documents/ListingDocumentsPanel'
import { ShipmentLabels } from '@/features/labels/ShipmentLabels'
import { SharePanel } from '@/features/share/SharePanel'
import { RouteGuidePanel } from '@/features/shipment-details-panel/RouteGuidePanel'
import { SpotQuoteRequestPanel } from '@/features/spot-quote'
import { useLoad } from '@/hooks/shipments'

import InfoBox from '../InfoBox'
import { ActiveLoadDetail } from './ActiveLoadDetail'
import { DetailPanelLayout } from './DetailPanelLayout'
import MessagesNotesTagsPanel from './MessagesNotesTagsPanel'
import { PendingLoadDetail } from './PendingLoadDetail'
import QuoteHistoryPanel from './QuoteHistoryPanel'
import UpdateTrackingPanel from './UpdateTrackingPanel'

export type PanelName =
  | 'confirm'
  | 'detail'
  | 'label'
  | 'message'
  | 'quote'
  | 'request'
  | 'routeGuide'
  | 'share'
  | 'track'
  | 'upload'

const detailPanel = 'detail'
const panelComponents: Record<
  PanelName,
  ((p: PropsWithLoad<{ closePanel: () => void }>) => ReactNode) | null
> = {
  confirm: p => (
    <DetailPanelLayout>
      <ConfirmationPanel {...p} />
    </DetailPanelLayout>
  ),
  detail: null,
  label: null,
  message: MessagesNotesTagsPanel,
  quote: QuoteHistoryPanel,
  request: SpotQuoteRequestPanel,
  routeGuide: null,
  share: SharePanel,
  track: UpdateTrackingPanel,
  upload: ListingDocumentsPanel,
}

type LoadDetailPanelProps = {
  loadId: string | null
}

const LoadDetailPanel = ({ loadId }: LoadDetailPanelProps) => {
  const loadQuery = useLoad(loadId)
  const [activePanel, setActivePanel] = useState<PanelName>(detailPanel)
  const closePanel = useCallback(() => setActivePanel(detailPanel), [])
  useOlarkHelper({ loadId })

  if (!loadId || loadQuery.isLoading || !loadQuery.data) {
    return (
      <DetailPanelLayout>
        {!loadId && <InfoBox>Please select a shipment.</InfoBox>}

        {loadId &&
          (loadQuery.isFetching ? (
            <InfoBox icon="sync">Loading...</InfoBox>
          ) : loadQuery.error.status === 403 ? (
            <InfoBox>
              You do not currently have access to view the BOL and labels.{' '}
              {supportMessageQuestions}
            </InfoBox>
          ) : (
            <InfoBox>Unable to load shipment, {supportMessage}</InfoBox>
          ))}
      </DetailPanelLayout>
    )
  }

  const ActivePanelComponent = panelComponents[activePanel]

  if (ActivePanelComponent) {
    return (
      <ActivePanelComponent closePanel={closePanel} load={loadQuery.data} />
    )
  }

  const isInProcess =
    loadQuery.data.status === 'awarded' || loadQuery.data.status === 'pending'

  return (
    <>
      {isInProcess ? (
        <PendingLoadDetail
          load={loadQuery.data}
          setActivePanel={setActivePanel}
        />
      ) : (
        <ActiveLoadDetail
          load={loadQuery.data}
          setActivePanel={setActivePanel}
        />
      )}

      <ShipperSlider onClose={closePanel} isOpen={activePanel === 'label'}>
        <ShipmentLabels load={loadQuery.data} setActiveSlider={closePanel} />
      </ShipperSlider>

      <ShipperSlider
        flush
        onClose={closePanel}
        isOpen={activePanel === 'routeGuide'}
      >
        <RouteGuidePanel load={loadQuery.data} setActiveSlider={closePanel} />
      </ShipperSlider>
    </>
  )
}

export default LoadDetailPanel
